import { type AppendMessage, type ThreadMessageLike, useExternalStoreRuntime } from "@assistant-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GcsAttachmentAdapter } from "./attachments/GcsAttachmentAdapter";
import { DeepgramDictationAdapter } from "./dictation/DeepgramDictationAdapter";

// ── Internal message types ──────────────────────────────────────────────

export interface WidgetItem {
  id: string;
  type: "followup_questions" | "sources" | "ehr_transfer_status";
  data: unknown;
}

export interface AttachmentInfo {
  id: string;
  filename: string;
  contentType?: string;
  size?: number;
}

export interface InternalMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
  widgets?: WidgetItem[];
  attachments?: AttachmentInfo[];
}

// ── SSE event shapes from the backend ────────────────────────────────────

interface SSEThreadCreated {
  type: "thread.created";
  thread_id: string;
}
interface SSEUserMessage {
  type: "user_message";
  id: string;
  thread_id: string;
  created_at: string;
  content: string;
  attachments?: Array<{ id: string; filename: string; mime_type?: string; size?: number }>;
}
interface SSEAssistantStart {
  type: "assistant_message.start";
  id: string;
  thread_id: string;
  created_at: string;
}
interface SSEDelta {
  type: "delta";
  delta: string;
}
interface SSEAssistantDone {
  type: "assistant_message.done";
  id: string;
  thread_id: string;
  created_at: string;
  content: string;
}
interface SSEWidget {
  type: "widget";
  id: string;
  widget_type: "followup_questions" | "sources" | "ehr_transfer_status";
  data: unknown;
}
interface SSEError {
  type: "error";
  message: string;
}

type SSEEvent =
  | SSEThreadCreated
  | SSEUserMessage
  | SSEAssistantStart
  | SSEDelta
  | SSEAssistantDone
  | SSEWidget
  | SSEError;

// ── Thread data fetching ─────────────────────────────────────────────────

async function fetchThreadMessages(
  baseUrl: string,
  threadId: string,
  headers: Record<string, string>,
): Promise<InternalMessage[]> {
  const res = await fetch(`${baseUrl}/superdoc/`, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify({
      type: "threads.get_by_id",
      params: { thread_id: threadId },
    }),
  });

  if (!res.ok) return [];
  const thread = await res.json();

  const rawMessages = thread.messages || [];
  const rawWidgets = thread.widgets || [];

  const messages: InternalMessage[] = rawMessages.map(
    (m: {
      id: string;
      role: string;
      content: string;
      created_at: string;
      attachments?: Array<{ id: string; filename: string; mime_type?: string; size?: number }>;
    }) => ({
      id: m.id,
      role: m.role as "user" | "assistant",
      content: m.content,
      createdAt: m.created_at,
      attachments:
        m.attachments && m.attachments.length > 0
          ? m.attachments.map((a) => ({ id: a.id, filename: a.filename, contentType: a.mime_type, size: a.size }))
          : undefined,
    }),
  );

  // Attach widgets to the last assistant message
  if (rawWidgets.length > 0) {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") {
        messages[i] = {
          ...messages[i],
          widgets: rawWidgets.map((w: { id: string; widget_type: string; data: unknown }) => ({
            id: w.id,
            type: w.widget_type as WidgetItem["type"],
            data: w.data,
          })),
        };
        break;
      }
    }
  }

  return messages;
}

async function fetchThreadList(
  baseUrl: string,
  headers: Record<string, string>,
): Promise<Array<{ id: string; title: string }>> {
  const res = await fetch(`${baseUrl}/superdoc/`, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify({ type: "threads.list", params: {} }),
  });
  if (!res.ok) return [];
  const data = await res.json();
  return (data.data || []).map((t: Record<string, unknown>) => ({
    id: t.id as string,
    title: t.title as string,
  }));
}

// ── SSE stream parser ────────────────────────────────────────────────────

function parseSSEEvents(text: string): SSEEvent[] {
  const events: SSEEvent[] = [];
  const lines = text.split("\n");
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed.startsWith("data: ")) continue;
    const jsonStr = trimmed.slice(6);
    if (!jsonStr || jsonStr === "[DONE]") continue;
    try {
      events.push(JSON.parse(jsonStr) as SSEEvent);
    } catch {
      // skip malformed
    }
  }
  return events;
}

// ── Hook ─────────────────────────────────────────────────────────────────

interface UseSuperdocRuntimeOptions {
  baseUrl: string;
  clientId?: number;
  initialThreadId: string | null;
}

export function useSuperdocRuntime({ baseUrl, clientId, initialThreadId }: UseSuperdocRuntimeOptions) {
  const [messages, setMessages] = useState<InternalMessage[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(initialThreadId);
  const abortRef = useRef<AbortController | null>(null);

  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;
  const isRunningRef = useRef(isRunning);
  isRunningRef.current = isRunning;

  const getHeaders = useCallback(() => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = localStorage.getItem("token");
    if (token) headers["Authorization"] = `Token ${token}`;
    if (clientId) headers["X-Client-ID"] = clientId.toString();
    return headers;
  }, [clientId]);

  // Load initial thread messages
  useEffect(() => {
    if (!initialThreadId) return;
    let cancelled = false;

    fetchThreadMessages(baseUrl, initialThreadId, getHeaders()).then((msgs) => {
      if (!cancelled) {
        setMessages(msgs);
        setThreadId(initialThreadId);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [baseUrl, initialThreadId, getHeaders]);

  // Process a single SSE event and update state
  const handleSSEEvent = useCallback(
    (event: SSEEvent, ctx: { assistantText: string; assistantId: string | null; widgets: WidgetItem[] }) => {
      switch (event.type) {
        case "thread.created":
          setThreadId(event.thread_id);
          break;

        case "user_message": {
          const doneAttachments: AttachmentInfo[] | undefined =
            event.attachments && event.attachments.length > 0
              ? event.attachments.map((a) => ({
                  id: a.id,
                  filename: a.filename,
                  contentType: a.mime_type,
                  size: a.size,
                }))
              : undefined;
          setMessages((prev) => {
            if (prev.some((m) => m.id === event.id)) return prev;
            const optimistic = prev.find(
              (m) => m.role === "user" && m.content === event.content && m.id.startsWith("optimistic_"),
            );
            if (optimistic) {
              return prev.map((m) =>
                m.id === optimistic.id
                  ? { ...m, id: event.id, createdAt: event.created_at, attachments: m.attachments || doneAttachments }
                  : m,
              );
            }
            return [
              ...prev,
              {
                id: event.id,
                role: "user" as const,
                content: event.content,
                createdAt: event.created_at,
                attachments: doneAttachments,
              },
            ];
          });
          break;
        }

        case "assistant_message.start":
          ctx.assistantId = event.id;
          ctx.assistantText = "";
          setMessages((prev) => [
            ...prev,
            { id: event.id, role: "assistant", content: "", createdAt: event.created_at },
          ]);
          break;

        case "delta": {
          ctx.assistantText += event.delta;
          const snap = ctx.assistantText;
          const id = ctx.assistantId;
          if (id) setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, content: snap } : m)));
          break;
        }

        case "assistant_message.done":
          setMessages((prev) => prev.map((m) => (m.id === event.id ? { ...m, content: event.content } : m)));
          ctx.assistantId = null;
          ctx.assistantText = "";
          break;

        case "widget":
          ctx.widgets.push({ id: event.id, type: event.widget_type, data: event.data });
          break;

        case "error":
          console.error("[Superdoc] SSE error:", event.message);
          break;
      }
    },
    [],
  );

  // Flush pending widgets onto last assistant message
  const flushWidgets = useCallback((widgets: WidgetItem[]) => {
    if (widgets.length === 0) return;
    setMessages((prev) => {
      const copy = [...prev];
      for (let i = copy.length - 1; i >= 0; i--) {
        if (copy[i].role === "assistant") {
          copy[i] = {
            ...copy[i],
            widgets: [...(copy[i].widgets || []), ...widgets],
          };
          break;
        }
      }
      return copy;
    });
  }, []);

  // Stream SSE response using ReadableStream (progressive) with fallback to text
  const processResponse = useCallback(
    async (response: Response) => {
      const ctx = { assistantText: "", assistantId: null as string | null, widgets: [] as WidgetItem[] };

      const contentType = response.headers.get("content-type") || "";

      // If the response is NOT event-stream (e.g. JSON error), handle it directly
      if (!contentType.includes("text/event-stream")) {
        console.warn("[Superdoc] Non-streaming response:", contentType);
        try {
          const json = await response.json();
          console.warn("[Superdoc] Response body:", json);
        } catch {
          const text = await response.text();
          console.warn("[Superdoc] Response text:", text);
        }
        return;
      }

      // Try ReadableStream first (progressive streaming)
      if (response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed.startsWith("data: ")) continue;
              const jsonStr = trimmed.slice(6);
              if (!jsonStr || jsonStr === "[DONE]") continue;
              try {
                const event = JSON.parse(jsonStr) as SSEEvent;
                handleSSEEvent(event, ctx);
              } catch {
                // skip malformed
              }
            }
          }

          // Process remaining buffer
          if (buffer.trim()) {
            for (const event of parseSSEEvents(buffer)) {
              handleSSEEvent(event, ctx);
            }
          }
        } catch (err) {
          // ReadableStream may fail (e.g. network error) - try fallback
          console.warn("[Superdoc] Stream read error, processing what we have:", err);
        }
      } else {
        // Fallback: read entire body as text and parse all events at once
        console.warn("[Superdoc] No ReadableStream, falling back to text()");
        const text = await response.text();
        for (const event of parseSSEEvents(text)) {
          handleSSEEvent(event, ctx);
        }
      }

      flushWidgets(ctx.widgets);
    },
    [handleSSEEvent, flushWidgets],
  );

  // Send a user message - this is called by the assistant-ui runtime
  const onNew = useCallback(
    async (message: AppendMessage) => {
      if (message.content.length === 0) return;

      let userText = "";
      for (const part of message.content) {
        if (part.type === "text") {
          userText += (part as { type: "text"; text: string }).text;
        }
      }
      if (!userText.trim()) return;

      // Extract attachment IDs from completed attachments
      const attachmentIds: string[] = [];
      if ("attachments" in message && Array.isArray(message.attachments)) {
        for (const att of message.attachments) {
          if (att.id && !att.id.startsWith("pending_")) {
            attachmentIds.push(att.id);
          }
        }
      }

      // Build attachment info for display
      const attachmentInfos: AttachmentInfo[] = [];
      if ("attachments" in message && Array.isArray(message.attachments)) {
        for (const att of message.attachments) {
          attachmentInfos.push({
            id: att.id,
            filename: att.name,
            contentType: att.contentType,
            size: att.file?.size,
          });
        }
      }

      // Add optimistic user message
      const optimisticId = `optimistic_${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        {
          id: optimisticId,
          role: "user",
          content: userText,
          createdAt: new Date().toISOString(),
          attachments: attachmentInfos.length > 0 ? attachmentInfos : undefined,
        },
      ]);

      setIsRunning(true);

      try {
        const currentThreadId = threadIdRef.current;
        const requestType = currentThreadId ? "threads.add_user_message" : "threads.create";

        const inputMetadata: Record<string, unknown> = {};
        if (followupUsedRef.current) {
          inputMetadata.source = "followup";
        } else if (dictationUsedRef.current) {
          inputMetadata.source = "dictation";
        }
        dictationUsedRef.current = false;
        followupUsedRef.current = false;

        const body = {
          type: requestType,
          params: {
            ...(currentThreadId ? { thread_id: currentThreadId } : {}),
            input: {
              content: [{ type: "input_text", text: userText }],
              attachments: attachmentIds.map((id) => ({ id })),
              ...(Object.keys(inputMetadata).length > 0 && {
                metadata: inputMetadata,
              }),
            },
          },
        };

        const controller = new AbortController();
        abortRef.current = controller;

        const response = await fetch(`${baseUrl}/superdoc/`, {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("[Superdoc] HTTP error:", response.status, errorText);
          return;
        }

        await processResponse(response);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") return;
        console.error("[Superdoc] Send message error:", err);
      } finally {
        setIsRunning(false);
        abortRef.current = null;
      }
    },
    [baseUrl, getHeaders, processResponse],
  );

  // Cancel current stream
  const onCancel = useCallback(async () => {
    abortRef.current?.abort();
    setIsRunning(false);
  }, []);

  // Send a custom action (clinical review, EHR status, etc.)
  const sendCustomAction = useCallback(
    async (action: { type: string; payload?: Record<string, unknown> }) => {
      const currentThreadId = threadIdRef.current || `new_${Date.now()}`;

      setIsRunning(true);
      try {
        const body = {
          type: "threads.custom_action",
          params: {
            thread_id: currentThreadId,
            action,
          },
        };

        const controller = new AbortController();
        abortRef.current = controller;

        const response = await fetch(`${baseUrl}/superdoc/`, {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) {
          console.error("[Superdoc] Custom action HTTP error:", response.status);
          return;
        }

        await processResponse(response);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") return;
        console.error("[Superdoc] Custom action error:", err);
      } finally {
        setIsRunning(false);
        abortRef.current = null;
      }
    },
    [baseUrl, getHeaders, processResponse],
  );

  // Switch to a different thread
  const switchThread = useCallback(
    async (newThreadId: string) => {
      const msgs = await fetchThreadMessages(baseUrl, newThreadId, getHeaders());
      setMessages(msgs);
      setThreadId(newThreadId);
    },
    [baseUrl, getHeaders],
  );

  // Create new thread — reset is handled below after runtime is created
  const runtimeRef = useRef<ReturnType<typeof useExternalStoreRuntime> | null>(null);
  const createNewThread = useCallback(async () => {
    abortRef.current?.abort();
    setMessages([]);
    setThreadId(null);
    setIsRunning(false);
    try {
      await runtimeRef.current?.thread.composer.reset();
    } catch {
      // Composer may not be initialized yet
    }
  }, []);

  // Get thread list
  const getThreadList = useCallback(() => fetchThreadList(baseUrl, getHeaders()), [baseUrl, getHeaders]);

  // Convert internal messages to assistant-ui format
  const convertMessage = useCallback(
    (msg: InternalMessage): ThreadMessageLike => ({
      role: msg.role,
      id: msg.id,
      content: [{ type: "text", text: msg.content }],
      createdAt: new Date(msg.createdAt),
    }),
    [],
  );

  const attachmentAdapter = useMemo(
    () =>
      new GcsAttachmentAdapter({
        baseUrl,
        getHeaders,
        getThreadId: () => threadIdRef.current,
      }),
    [baseUrl, getHeaders],
  );

  // Track whether dictation (mic) was used for the current message
  const dictationUsedRef = useRef(false);
  // Track whether the message originated from a follow-up question click
  const followupUsedRef = useRef(false);
  const markFollowupUsed = useCallback(() => {
    followupUsedRef.current = true;
  }, []);

  const dictationAdapter = useMemo(() => {
    const adapter = new DeepgramDictationAdapter({ baseUrl, getHeaders });
    // Wrap listen() to detect when dictation is used
    const originalListen = adapter.listen.bind(adapter);
    adapter.listen = () => {
      dictationUsedRef.current = true;
      return originalListen();
    };
    return adapter;
  }, [baseUrl, getHeaders]);

  const runtime = useExternalStoreRuntime({
    messages,
    isRunning,
    onNew,
    onCancel,
    convertMessage,
    adapters: {
      attachments: attachmentAdapter,
      dictation: dictationAdapter,
    },
  });
  runtimeRef.current = runtime;

  return useMemo(
    () => ({
      runtime,
      messages,
      threadId,
      isRunning,
      sendCustomAction,
      switchThread,
      createNewThread,
      getThreadList,
      isRunningRef,
      markFollowupUsed,
    }),
    [
      runtime,
      messages,
      threadId,
      isRunning,
      sendCustomAction,
      switchThread,
      createNewThread,
      getThreadList,
      markFollowupUsed,
    ],
  );
}
